import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Draggable } from '@hello-pangea/dnd';
import axios from '../lib/axios';
import StrictModeDroppable from '../components/common/StrictModeDroppable';
import { CURRENCIES, currencyOptionLabel, tenantDefaultCurrency } from '../lib/currencies';
import { lineTotals, quoteTotals, formatMoney } from '../lib/quoteTotals';
import { useAuthStore } from '../store/useAuthStore';
import { useModules } from '../lib/useModules';
import SendQuoteModal from '../components/quotes/SendQuoteModal';
import ContactPicker from '../components/common/ContactPicker';

/** Identificador local estable para los renglones aún no guardados. */
let tempId = 0;
const nextTempId = () => `tmp-${++tempId}`;

function lineFromCatalogItem(item) {
    return {
        key: nextTempId(),
        catalog_item_id: item.id,
        name: item.name,
        description: item.description || '',
        quantity: 1,
        // Un ítem de precio abierto entra en 0 y el usuario escribe el importe.
        unit_price: item.unit_price === null ? 0 : Number(item.unit_price),
        discount_percent: 0,
        tax_rate: Number(item.tax_rate ?? 5),
        is_open_price: item.unit_price === null,
    };
}

export default function QuoteBuilder() {
    const { id } = useParams();
    const isNew = !id || id === 'new';
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const qc = useQueryClient();
    const tenant = useAuthStore((s) => s.tenant);
    const { isFeatureEnabled } = useModules();

    const [search, setSearch] = useState('');
    const [lines, setLines] = useState([]);
    const [header, setHeader] = useState({
        // Se llega con ?contact_id= al crear una cotización desde la ficha del
        // contacto: así el cliente viene ya elegido y no hay que buscarlo.
        contact_id: searchParams.get('contact_id') || '',
        currency: tenantDefaultCurrency(tenant),
        issue_date: new Date().toISOString().slice(0, 10),
        valid_until: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
        intro: '',
        notes: '',
    });
    const [status, setStatus] = useState('draft');
    const [number, setNumber] = useState(null);

    const { data: catalogResp } = useQuery({
        queryKey: ['catalog-items'],
        queryFn: () => axios.get('/api/catalog-items'),
    });
    const catalog = catalogResp?.data || [];

    const { data: quoteResp } = useQuery({
        queryKey: ['quote', id],
        queryFn: () => axios.get(`/api/quotes/${id}`),
        enabled: !isNew,
    });

    // Carga inicial de una cotización existente en el estado del constructor.
    useEffect(() => {
        const q = quoteResp?.data;
        if (!q) return;

        setNumber(q.number);
        setStatus(q.status);
        setHeader({
            contact_id: q.contact_id ?? '',
            currency: q.currency,
            issue_date: q.issue_date?.slice(0, 10) ?? '',
            valid_until: q.valid_until?.slice(0, 10) ?? '',
            intro: q.intro ?? '',
            notes: q.notes ?? '',
        });
        setLines((q.lines || []).map((l) => ({
            key: nextTempId(),
            catalog_item_id: l.catalog_item_id,
            name: l.name,
            description: l.description || '',
            quantity: Number(l.quantity),
            unit_price: Number(l.unit_price),
            discount_percent: Number(l.discount_percent),
            tax_rate: Number(l.tax_rate),
        })));
    }, [quoteResp]);

    const grouped = useMemo(() => {
        const term = search.trim().toLowerCase();
        const filtered = catalog.filter((i) =>
            !term ||
            i.name?.toLowerCase().includes(term) ||
            i.category?.toLowerCase().includes(term) ||
            i.description?.toLowerCase().includes(term));

        return filtered.reduce((acc, item) => {
            const cat = item.category || 'Sin categoría';
            (acc[cat] ||= []).push(item);
            return acc;
        }, {});
    }, [catalog, search]);

    const totals = useMemo(() => quoteTotals(lines), [lines]);

    const saveMutation = useMutation({
        mutationFn: () => {
            const payload = {
                ...header,
                contact_id: header.contact_id || null,
                lines: lines.map(({ key, is_open_price, ...rest }) => rest),
            };
            return isNew
                ? axios.post('/api/quotes', payload)
                : axios.put(`/api/quotes/${id}`, payload);
        },
        onSuccess: (response) => {
            qc.invalidateQueries({ queryKey: ['quotes'] });
            if (isNew) navigate(`/quotes/${response.data.id}`, { replace: true });
            else qc.invalidateQueries({ queryKey: ['quote', id] });
        },
        onError: (error) => alert(error.response?.data?.message || 'No se pudo guardar la cotización.'),
    });

    const onDragEnd = ({ source, destination }) => {
        if (!destination) return;

        // Del catálogo al presupuesto: se agrega una copia.
        if (source.droppableId.startsWith('cat:') && destination.droppableId === 'quote') {
            const category = source.droppableId.slice(4);
            const item = grouped[category]?.[source.index];
            if (!item) return;

            setLines((prev) => {
                const next = [...prev];
                next.splice(destination.index, 0, lineFromCatalogItem(item));
                return next;
            });
            return;
        }

        // Reordenar dentro del presupuesto.
        if (source.droppableId === 'quote' && destination.droppableId === 'quote') {
            setLines((prev) => {
                const next = [...prev];
                const [moved] = next.splice(source.index, 1);
                next.splice(destination.index, 0, moved);
                return next;
            });
        }
    };

    const updateLine = (key, patch) =>
        setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

    const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

    const addBlankLine = () => setLines((prev) => [...prev, {
        key: nextTempId(), catalog_item_id: null, name: '', description: '',
        quantity: 1, unit_price: 0, discount_percent: 0, tax_rate: 5,
    }]);

    const locked = status === 'accepted' || status === 'rejected';

    // El backend exige nombre en cada renglón: sin esta guarda el usuario
    // llegaba a un 422 poco claro después de haber armado toda la cotización.
    const unnamedLines = lines.filter((l) => !l.name.trim()).length;
    const openPriceMissing = lines.filter((l) => l.is_open_price && !Number(l.unit_price)).length;
    const canSave = lines.length > 0 && unnamedLines === 0 && !locked;

    const [openingPdf, setOpeningPdf] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const canSendEmail = isFeatureEnabled('quotes', 'send_email');

    /**
     * El endpoint del PDF exige el Bearer token (ver lib/axios.js), así que un
     * window.open directo a la URL devolvería 401: hay que pedirlo como blob
     * autenticado y abrir ese blob.
     */
    const openPdf = async () => {
        setOpeningPdf(true);
        try {
            const response = await axios.get(`/api/quotes/${id}/pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            window.open(url, '_blank');
            // Se revoca con retraso: revocarlo en el acto corta la carga en la
            // pestaña recién abierta.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            alert('No se pudo generar el PDF.');
        } finally {
            setOpeningPdf(false);
        }
    };

    return (
        <div className="flex flex-col h-screen bg-background-light dark:bg-background-dark">
            {/* Barra superior */}
            <div className="shrink-0 border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between gap-4 bg-white dark:bg-surface-dark">
                <div className="flex items-center gap-3 min-w-0">
                    <button onClick={() => navigate('/quotes')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
                        <span className="material-symbols-outlined">arrow_back</span>
                    </button>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight text-slate-900 dark:text-white truncate">
                            {number || 'Nueva cotización'}
                        </h1>
                        <p className="text-xs text-gray-400">
                            {lines.length} {lines.length === 1 ? 'renglón' : 'renglones'} · {header.currency}
                        </p>
                    </div>
                    {locked && (
                        <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                            Bloqueada
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {!isNew && canSendEmail && (
                        <button
                            onClick={() => setSendOpen(true)}
                            disabled={saveMutation.isPending}
                            title={quoteResp?.data?.contact_id ? undefined : 'La cotización no tiene cliente asignado; vas a poder escribir el correo a mano.'}
                            className="py-2 px-4 border border-gray-200 dark:border-gray-700 text-sm font-bold rounded-xl flex items-center gap-2 dark:text-white"
                        >
                            <span className="material-symbols-outlined text-lg">send</span> Enviar
                        </button>
                    )}
                    {!isNew && (
                        <button
                            onClick={openPdf}
                            disabled={openingPdf}
                            className="py-2 px-4 border border-gray-200 dark:border-gray-700 text-sm font-bold rounded-xl flex items-center gap-2 dark:text-white disabled:opacity-50"
                        >
                            <span className="material-symbols-outlined text-lg">picture_as_pdf</span>
                            {openingPdf ? 'Generando...' : 'PDF'}
                        </button>
                    )}
                    <button
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending || !canSave}
                        title={unnamedLines > 0 ? 'Hay renglones sin nombre' : undefined}
                        className="py-2 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-50"
                    >
                        {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
                    </button>
                </div>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex flex-1 overflow-hidden">
                    {/* Catálogo */}
                    <aside className="w-80 shrink-0 border-r border-gray-200 dark:border-gray-800 flex flex-col">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                            <div className="relative">
                                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">search</span>
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar en el catálogo"
                                    className="w-full text-sm pl-10 pr-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 space-y-4">
                            {Object.keys(grouped).length === 0 && (
                                <p className="text-xs text-gray-400 px-2 py-6 text-center">
                                    {catalog.length === 0
                                        ? 'El catálogo está vacío. Agregá ítems desde el listado de cotizaciones.'
                                        : 'Ningún ítem coincide con la búsqueda.'}
                                </p>
                            )}

                            {Object.entries(grouped).map(([category, items]) => (
                                <div key={category}>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-2 mb-1.5">{category}</p>
                                    <StrictModeDroppable droppableId={`cat:${category}`} isDropDisabled>
                                        {(dropProvided) => (
                                            <div ref={dropProvided.innerRef} {...dropProvided.droppableProps} className="space-y-1.5">
                                                {items.map((item, index) => (
                                                    <Draggable key={item.id} draggableId={`cat-${item.id}`} index={index} isDragDisabled={locked}>
                                                        {(dragProvided, snapshot) => (
                                                            <div
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                {...dragProvided.dragHandleProps}
                                                                onDoubleClick={() => !locked && setLines((p) => [...p, lineFromCatalogItem(item)])}
                                                                className={`p-2.5 rounded-xl border text-left cursor-grab active:cursor-grabbing transition-colors ${
                                                                    snapshot.isDragging
                                                                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 shadow-lg'
                                                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800/40 hover:border-blue-300'
                                                                }`}
                                                            >
                                                                <p className="text-xs font-bold dark:text-white leading-snug">{item.name}</p>
                                                                <div className="flex items-center justify-between mt-1">
                                                                    <span className="text-[11px] text-gray-500">
                                                                        {item.unit_price === null
                                                                            ? 'Precio abierto'
                                                                            : formatMoney(item.unit_price, item.currency)}
                                                                    </span>
                                                                    <span className="text-[10px] text-gray-400">IVA {Number(item.tax_rate)}%</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {dropProvided.placeholder}
                                            </div>
                                        )}
                                    </StrictModeDroppable>
                                </div>
                            ))}
                        </div>
                        <p className="p-3 text-[10px] text-gray-400 border-t border-gray-100 dark:border-gray-800">
                            Arrastrá un ítem al presupuesto, o hacé doble clic para agregarlo al final.
                        </p>
                    </aside>

                    {/* Presupuesto */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="max-w-4xl mx-auto p-6 space-y-5">
                            {/* Cabecera */}
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                <div className="sm:col-span-2">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">Cliente</label>
                                    <ContactPicker
                                        value={header.contact_id}
                                        initialContact={quoteResp?.data?.contact ?? null}
                                        disabled={locked}
                                        onChange={(contactId) => setHeader((h) => ({ ...h, contact_id: contactId }))}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">Moneda</label>
                                    <select
                                        value={header.currency}
                                        onChange={(e) => setHeader({ ...header, currency: e.target.value })}
                                        disabled={locked}
                                        className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white disabled:opacity-60"
                                    >
                                        {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">Válida hasta</label>
                                    <input
                                        type="date"
                                        value={header.valid_until}
                                        onChange={(e) => setHeader({ ...header, valid_until: e.target.value })}
                                        disabled={locked}
                                        className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white disabled:opacity-60"
                                    />
                                </div>
                            </div>

                            <textarea
                                value={header.intro}
                                onChange={(e) => setHeader({ ...header, intro: e.target.value })}
                                disabled={locked}
                                rows={2}
                                placeholder="Párrafo de presentación (opcional): recomendación, contexto..."
                                className="w-full text-sm p-3 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white resize-none disabled:opacity-60"
                            />

                            {/* Renglones */}
                            <StrictModeDroppable droppableId="quote">
                                {(dropProvided, dropSnapshot) => (
                                    <div
                                        ref={dropProvided.innerRef}
                                        {...dropProvided.droppableProps}
                                        className={`rounded-2xl border-2 border-dashed transition-colors min-h-[160px] p-2 space-y-2 ${
                                            dropSnapshot.isDraggingOver
                                                ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
                                                : 'border-gray-200 dark:border-gray-700'
                                        }`}
                                    >
                                        {lines.length === 0 && !dropSnapshot.isDraggingOver && (
                                            <div className="h-[150px] flex flex-col items-center justify-center text-center gap-1">
                                                <span className="material-symbols-outlined text-3xl text-gray-300">drag_pan</span>
                                                <p className="text-sm text-gray-400">Arrastrá ítems del catálogo hasta acá</p>
                                            </div>
                                        )}

                                        {lines.map((line, index) => {
                                            const t = lineTotals(line);
                                            return (
                                                <Draggable key={line.key} draggableId={line.key} index={index} isDragDisabled={locked}>
                                                    {(dragProvided, snapshot) => (
                                                        <div
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                            className={`bg-white dark:bg-slate-800/60 border rounded-xl p-3 ${
                                                                snapshot.isDragging
                                                                    ? 'border-blue-400 shadow-xl'
                                                                    : !line.name.trim()
                                                                        ? 'border-amber-300 dark:border-amber-700'
                                                                        : 'border-gray-200 dark:border-gray-700'
                                                            }`}
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <div {...dragProvided.dragHandleProps} className="pt-1.5 text-gray-300 cursor-grab active:cursor-grabbing">
                                                                    <span className="material-symbols-outlined text-lg">drag_indicator</span>
                                                                </div>

                                                                <div className="flex-1 min-w-0 space-y-2">
                                                                    <input
                                                                        value={line.name}
                                                                        onChange={(e) => updateLine(line.key, { name: e.target.value })}
                                                                        disabled={locked}
                                                                        placeholder="Concepto"
                                                                        className="w-full text-sm font-bold bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-blue-400 outline-none dark:text-white py-0.5"
                                                                    />
                                                                    <textarea
                                                                        value={line.description}
                                                                        onChange={(e) => updateLine(line.key, { description: e.target.value })}
                                                                        disabled={locked}
                                                                        rows={line.description ? 2 : 1}
                                                                        placeholder="Detalle (opcional)"
                                                                        className="w-full text-xs bg-transparent border-0 outline-none text-gray-500 dark:text-gray-400 resize-none"
                                                                    />

                                                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                                        <NumField label="Cant." value={line.quantity} disabled={locked}
                                                                            onChange={(v) => updateLine(line.key, { quantity: v })} />
                                                                        <NumField label="Precio" value={line.unit_price} disabled={locked}
                                                                            highlight={line.is_open_price && !line.unit_price}
                                                                            onChange={(v) => updateLine(line.key, { unit_price: v })} />
                                                                        <NumField label="Desc. %" value={line.discount_percent} disabled={locked}
                                                                            onChange={(v) => updateLine(line.key, { discount_percent: v })} />
                                                                        <NumField label="IVA %" value={line.tax_rate} disabled={locked}
                                                                            onChange={(v) => updateLine(line.key, { tax_rate: v })} />
                                                                    </div>
                                                                </div>

                                                                <div className="text-right shrink-0 w-32">
                                                                    <p className="text-sm font-black dark:text-white">{formatMoney(t.total, header.currency)}</p>
                                                                    <p className="text-[10px] text-gray-400">
                                                                        base {formatMoney(t.taxable, header.currency)}
                                                                    </p>
                                                                    <button
                                                                        onClick={() => removeLine(line.key)}
                                                                        disabled={locked}
                                                                        className="mt-1 text-gray-300 hover:text-red-500 disabled:opacity-40"
                                                                    >
                                                                        <span className="material-symbols-outlined text-lg">delete</span>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {line.is_open_price && !line.unit_price && (
                                                                <p className="text-[11px] text-amber-600 mt-2 flex items-center gap-1">
                                                                    <span className="material-symbols-outlined text-sm">info</span>
                                                                    Este ítem no tiene precio fijo: escribí el importe.
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {dropProvided.placeholder}
                                    </div>
                                )}
                            </StrictModeDroppable>

                            {!locked && (
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <button
                                        onClick={addBlankLine}
                                        className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-blue-300 dark:border-blue-800"
                                    >
                                        <span className="material-symbols-outlined text-base">add</span>
                                        Agregar ítem manual
                                    </button>
                                    {unnamedLines > 0 && (
                                        <span className="text-xs text-amber-600 flex items-center gap-1">
                                            <span className="material-symbols-outlined text-sm">warning</span>
                                            {unnamedLines === 1
                                                ? 'Hay un renglón sin nombre'
                                                : `Hay ${unnamedLines} renglones sin nombre`}
                                        </span>
                                    )}
                                    {/* El aviso por renglón puede quedar fuera de pantalla en una
                                        cotización larga, y un ítem de precio abierto olvidado en 0
                                        se va al cliente como si fuera gratis. No bloquea el guardado
                                        —un ítem incluido sin cargo es válido— pero se ve siempre. */}
                                    {openPriceMissing > 0 && (
                                        <span className="text-xs text-amber-600 flex items-center gap-1">
                                            <span className="material-symbols-outlined text-sm">price_change</span>
                                            {openPriceMissing === 1
                                                ? 'Hay un ítem de precio abierto sin importe'
                                                : `Hay ${openPriceMissing} ítems de precio abierto sin importe`}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Totales */}
                            <div className="flex justify-end">
                                <div className="w-full sm:w-72 space-y-1.5 bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-gray-700 rounded-2xl p-4">
                                    <Row label="Base imponible" value={formatMoney(totals.subtotal, header.currency)} />
                                    <Row label="IVA" value={formatMoney(totals.totalTax, header.currency)} />
                                    <div className="pt-2 mt-1 border-t-2 border-gray-900 dark:border-white flex justify-between items-baseline">
                                        <span className="text-sm font-black dark:text-white">Total</span>
                                        <span className="text-lg font-black dark:text-white">{formatMoney(totals.total, header.currency)}</span>
                                    </div>
                                </div>
                            </div>

                            <textarea
                                value={header.notes}
                                onChange={(e) => setHeader({ ...header, notes: e.target.value })}
                                disabled={locked}
                                rows={2}
                                placeholder="Notas al pie (opcional)"
                                className="w-full text-sm p-3 border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-slate-800 dark:text-white resize-none disabled:opacity-60"
                            />
                        </div>
                    </div>
                </div>
            </DragDropContext>

            {sendOpen && quoteResp?.data && (
                <SendQuoteModal
                    quote={quoteResp.data}
                    onClose={() => setSendOpen(false)}
                    onSent={() => {
                        setSendOpen(false);
                        // El envío marca la cotización como enviada del lado del
                        // servidor: hay que releerla para que se refleje acá.
                        qc.invalidateQueries({ queryKey: ['quote', id] });
                        qc.invalidateQueries({ queryKey: ['quotes'] });
                        setStatus('sent');
                    }}
                />
            )}
        </div>
    );
}

function NumField({ label, value, onChange, disabled, highlight }) {
    return (
        <label className="block">
            <span className="block text-[9px] font-black uppercase tracking-widest text-gray-400 mb-0.5">{label}</span>
            <input
                type="number"
                step="0.01"
                min="0"
                value={value}
                disabled={disabled}
                // Se guarda '' mientras el campo está vacío para que se pueda
                // borrar y reescribir; Number('') daría 0 y dejaría un cero
                // pegado adelante del número nuevo.
                onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                className={`w-full text-xs p-1.5 border rounded-lg dark:bg-slate-900 dark:text-white ${
                    highlight ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/10' : 'border-gray-200 dark:border-gray-700'
                }`}
            />
        </label>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between text-sm">
            <span className="text-gray-500">{label}</span>
            <span className="font-bold dark:text-white">{value}</span>
        </div>
    );
}
